import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Faq } from "../components/Faq";
import { HOME_FAQ, faqSchema, breadcrumbSchema } from "../data/site";
import { PRICING_FAQ, buildAllFaqs } from "../data/faqs";

const trail = [["Home", "/"], ["FAQ", "/faq"]];
const CATEGORIES = ["General", "Pricing", "State guides"];

export default function FaqPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("General");
  const allFaqs = useMemo(buildAllFaqs, []);

  const q = query.trim().toLowerCase();
  const words = q.split(/\s+/).filter(Boolean);
  const results = q
    ? allFaqs.filter((f) => {
        const hay = (f.q + " " + f.a + " " + f.category).toLowerCase();
        return words.every((w) => hay.includes(w));
      })
    : allFaqs.filter((f) =>
        category === "State guides"
          ? f.category !== "General" && f.category !== "Pricing"
          : f.category === category
      );
  const shown = results.slice(0, 60);

  return (
    <>
      <Seo
        title="Payroll FAQs & Help Center | ABSLpay"
        description="Search every ABSLpay payroll FAQ in one place: pricing, switching providers, multi-state rules, and state-by-state withholding, unemployment and local tax questions."
        path="/faq"
        jsonLd={[faqSchema([...HOME_FAQ, ...PRICING_FAQ]), breadcrumbSchema(trail)]}
      />
      <Breadcrumbs trail={trail} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,#ECFDF5_0%,transparent_50%)]" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 lg:pt-16">
          <MaskedLines
            lines={["Every payroll answer,", "in one place."]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
          />
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
            Search {allFaqs.length} questions across pricing, switching and all 50 states — or browse by category.
          </p>

          <div className="mt-10">
            <div className="relative max-w-lg">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="faq-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Try "local taxes in Ohio" or "cancellation fee"...'
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            {!q && (
              <div className="mt-5 flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`faq-category-${c.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setCategory(c)}
                    className={`rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${
                      category === c ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-slate-900"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400" data-testid="faq-result-count">
              {q ? `${results.length} result${results.length === 1 ? "" : "s"} for "${query.trim()}"` : category}
              {results.length > 60 && " · showing first 60 — refine your search"}
            </p>
            <div className="mt-4">
              {shown.length > 0 ? (
                <Faq qas={shown.map((f) => [`${f.category !== "General" && f.category !== "Pricing" ? f.category + ": " : ""}${f.q}`, f.a])} testPrefix="helpfaq" />
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="faq-no-results">
                  <p className="font-display text-lg font-bold text-slate-900">No matches for that search.</p>
                  <p className="mt-2 text-sm text-slate-600">
                    Try a shorter phrase, or{" "}
                    <Link to="/contact" className="font-semibold text-emerald-700 hover:underline">ask us directly</Link>{" "}
                    — a CPA answers every question.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
